import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  adminXStatus,
  adminXSetSettings,
  adminXPreview,
  adminXAnnounceBySlug,
} from "../lib/api";
import DailyInsightPanel from "./DailyInsightPanel";

// Operator console for X (Twitter) outreach. Announces newly-listed tools
// from @terminalvalue_1 with an @mention of the owner's handle (scraped from
// their site) + a link to their listing. Pay-per-use: a post with a link is
// ~$0.20, so a hard daily USD budget is enforced server-side.
export default function AdminXOutreach() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [slug, setSlug] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    adminXStatus()
      .then((d) => {
        setStatus(d);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    Promise.resolve().then(load);
  }, [load]);

  const toggleAuto = async () => {
    try {
      const next = !status?.auto_announce;
      await adminXSetSettings(next);
      setStatus((s) => ({ ...s, auto_announce: next }));
      toast.success(next ? "auto-announce on" : "auto-announce off");
    } catch {
      toast.error("could not update setting");
    }
  };

  const doPreview = async () => {
    const s = slug.trim();
    if (!s) return;
    setPreview(null);
    try {
      const d = await adminXPreview(s);
      setPreview(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "could not preview");
    }
  };

  const doAnnounce = async (force = false) => {
    const s = slug.trim();
    if (!s) return;
    setBusy(true);
    try {
      const d = await adminXAnnounceBySlug(s, force);
      toast.success("posted to X");
      setPreview({ ...preview, posted_url: d.url, text: d.text });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "could not post");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="text-[12px] text-[#888]">loading…</div>;
  }

  const recent = status?.recent || [];

  return (
    <div data-testid="admin-x-outreach" className="space-y-5">
      {/* daily editorial post engine */}
      <DailyInsightPanel />

      {/* config / budget bar */}
      <div className="border border-black p-3" data-testid="admin-x-status">
        <div className="text-[13px] font-bold">
          x outreach — posting as {status?.account}
        </div>
        {!status?.configured && (
          <p
            className="text-[11px] text-[#a00] mt-1 m-0"
            data-testid="admin-x-not-configured"
          >
            Not configured: add <code>X_ACCESS_TOKEN</code> and{" "}
            <code>X_ACCESS_TOKEN_SECRET</code> (OAuth 1.0a user context) to{" "}
            <code>backend/.env</code>, then restart the backend. The bearer
            token alone cannot post.
          </p>
        )}
        <div className="mt-2 text-[12px] text-[#444] flex flex-wrap gap-x-5 gap-y-1">
          <span>
            today: <b className="tabular-nums">{status?.count}</b> posts ·{" "}
            <b className="tabular-nums">${(status?.cost_usd ?? 0).toFixed(2)}</b> /
            ${(status?.budget ?? 0).toFixed(2)}
          </span>
          <span>
            remaining today:{" "}
            <b className="tabular-nums">${(status?.remaining ?? 0).toFixed(2)}</b>{" "}
            (~{Math.floor((status?.remaining ?? 0) / (status?.post_cost_usd || 0.2))} posts)
          </span>
        </div>
        <label
          className="mt-3 flex items-center gap-2 text-[12px] cursor-pointer select-none"
          data-testid="admin-x-auto-toggle"
        >
          <input
            type="checkbox"
            checked={!!status?.auto_announce}
            onChange={toggleAuto}
            disabled={!status?.configured}
          />
          auto-announce every newly-approved tool (only when we have its handle)
        </label>
      </div>

      {/* per-tool announce */}
      <div>
        <div className="text-[11px] uppercase tracking-wide text-[#888]">
          announce a tool by slug
        </div>
        <div className="mt-1 flex gap-2">
          <input
            type="text"
            value={slug}
            onChange={(e) => setSlug(e.target.value.trim())}
            placeholder="tool slug, e.g. alphasense"
            className="flex-1 border border-black px-3 py-2 text-[13px] outline-none focus:bg-[#FBF8EE]"
            data-testid="admin-x-slug"
            onKeyDown={(e) => e.key === "Enter" && doPreview()}
          />
          <button
            type="button"
            className="tv-action-link border border-black px-3"
            data-testid="admin-x-preview-btn"
            onClick={doPreview}
          >
            [preview]
          </button>
        </div>

        {preview && (
          <div className="mt-3 border border-black p-3" data-testid="admin-x-preview">
            <div className="text-[11px] uppercase tracking-wide text-[#888] flex justify-between">
              <span>preview</span>
              <span>
                handle:{" "}
                {preview.handle ? (
                  <span className="text-black">@{preview.handle}</span>
                ) : (
                  <span className="text-[#a00]">none scraped</span>
                )}
              </span>
            </div>
            <pre className="mt-1 p-2 border border-black bg-[#FBF8EE] text-[12px] whitespace-pre-wrap">
              {preview.text}
            </pre>
            {preview.announced_at && (
              <div className="text-[11px] text-[#a60] mt-1" data-testid="admin-x-already">
                already announced on {String(preview.announced_at).slice(0, 10)} —
                use force-announce to re-post.
              </div>
            )}
            {preview.posted_url && (
              <div className="text-[11px] mt-1" data-testid="admin-x-posted">
                posted:{" "}
                <a href={preview.posted_url} target="_blank" rel="noopener noreferrer" className="tv-link">
                  {preview.posted_url}
                </a>
              </div>
            )}
            <div className="mt-2 flex gap-3">
              <button
                type="button"
                className="tv-action-link border border-black px-3 py-1"
                data-testid="admin-x-announce-btn"
                disabled={busy || !status?.configured}
                onClick={() => doAnnounce(false)}
              >
                {busy ? "posting…" : "[announce on X]"}
              </button>
              {preview.announced_at && (
                <button
                  type="button"
                  className="tv-action-link border border-[#a00] text-[#a00] px-3 py-1"
                  data-testid="admin-x-force-btn"
                  disabled={busy || !status?.configured}
                  onClick={() => doAnnounce(true)}
                >
                  [force re-announce]
                </button>
              )}
            </div>
            <p className="text-[10px] text-[#888] mt-2 m-0">
              cost: ~${(status?.post_cost_usd ?? 0.2).toFixed(2)} (post contains a
              link). only approved/public tools can be announced.
            </p>
          </div>
        )}
      </div>

      {/* recent posts */}
      <div>
        <div className="text-[11px] uppercase tracking-wide text-[#888]">
          recent announcements
        </div>
        {recent.length === 0 ? (
          <div className="mt-2 text-[12px] text-[#888]" data-testid="admin-x-recent-empty">
            No announcements yet.
          </div>
        ) : (
          <table className="mt-2 w-full text-[12px] border-collapse" data-testid="admin-x-recent">
            <thead>
              <tr className="text-left border-b border-black">
                <th className="py-1 pr-2 font-bold">tool</th>
                <th className="py-1 pr-2 font-bold">@mention</th>
                <th className="py-1 pr-2 font-bold">posted</th>
                <th className="py-1 font-bold">link</th>
              </tr>
            </thead>
            <tbody>
              {recent.map((p) => (
                <tr key={p.post_id} className="border-b border-[#eee]" data-testid={`admin-x-post-${p.slug}`}>
                  <td className="py-1 pr-2">
                    <a href={`/tool/${p.slug}`} target="_blank" rel="noopener noreferrer" className="tv-link">
                      {p.title || p.slug}
                    </a>
                  </td>
                  <td className="py-1 pr-2 text-[#666]">{p.handle ? `@${p.handle}` : "—"}</td>
                  <td className="py-1 pr-2 text-[#666]">{(p.at || "").slice(0, 10)}</td>
                  <td className="py-1">
                    <a
                      href={`https://x.com/${(status?.account || "").replace("@", "")}/status/${p.post_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="tv-link"
                    >
                      view
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
